<template>
  <div
    class="w-full grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 border-b border-border bg-surface sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:gap-x-3"
    :class="[
      displaySubtitle ? 'min-h-14 py-2 sm:min-h-[60px]' : 'min-h-14',
      layoutCollapsedStyle?.value ? 'px-2' : 'px-3 sm:px-4 lg:px-6',
    ]"
  >
    <!-- Left: sidebar toggle + title / breadcrumbs -->
    <div class="flex min-w-0 items-center gap-2 sm:gap-3 justify-self-start">
      <button
        v-if="showCollapseToggle"
        type="button"
        class="w-9 h-9 shrink-0 flex items-center justify-center rounded-lg text-text hover:bg-surface-hover transition-colors"
        :aria-label="isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        @click="onSidebarToggle"
      >
        <i class="ri-menu-line text-xl" aria-hidden="true" />
      </button>

      <div
        class="flex min-w-0 flex-1 flex-col justify-center min-h-[1.25rem] transition-colors duration-150 ease-out"
      >
        <BaseBreadcrumbs
          v-if="hasTopBarBreadcrumbs"
          :items="topBarBreadcrumbItems"
          variant="emphasized"
          separator-variant="chevron"
          class="min-w-0"
        />
        <template v-else>
          <div class="flex min-w-0 items-center gap-2">
            <span
              class="truncate font-semibold text-text whitespace-nowrap min-w-0 tracking-tight"
              :class="displaySubtitle ? 'text-base leading-snug sm:text-[18px]' : 'text-base sm:text-lg'"
            >
              {{ displayTitle }}
            </span>
            <div id="topbar-title-extra" class="flex shrink-0 items-center" />
          </div>
          <span
            v-if="displaySubtitle"
            class="hidden truncate text-[12px] leading-snug text-text-muted whitespace-nowrap min-w-0 mt-0.5 sm:block"
          >
            {{ displaySubtitle }}
          </span>
        </template>
      </div>
    </div>

    <!-- Center: page-specific centered content (e.g. org chart stats) -->
    <div
      id="topbar-center"
      class="col-start-2 row-start-1 hidden min-w-0 max-w-full items-center justify-center justify-self-center px-1 sm:flex sm:px-2"
    />

    <!-- Right: optional extras + page actions -->
    <div
      class="col-start-2 row-start-1 flex min-w-0 shrink-0 items-center justify-end justify-self-end gap-1 sm:col-start-3 sm:gap-2"
    >
      <div
        id="topbar-breadcrumbs-extra"
        class="flex min-w-0 shrink-0 items-center justify-end gap-2"
      />

      <div id="topbar-page-actions" class="flex min-w-0 max-w-full flex-nowrap items-center justify-end" />
      <BasePageHeaderActions
        v-if="!actionsFromSlot && topBarActionsWithVariants.length"
        :actions="topBarActionsWithVariants"
        size="xs"
        class="topbar-actions topbar-actions--minimal whitespace-nowrap shrink-0 [&_button]:px-2 [&_button.bg-primary]:px-3 [&_button]:sm:px-4 [&_button]:py-2 [&_button]:text-sm [&_button]:font-medium [&_button]:rounded-lg [&_button]:min-h-9 [&_button]:transition-colors [&_button:not(.bg-primary)]:bg-transparent [&_button:not(.bg-primary)]:text-text-secondary [&_button:not(.bg-primary)]:hover:bg-surface-hover [&_.mr-2]:mr-1.5"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, onUnmounted, provide, watch } from 'vue'
import { useRoute } from 'vue-router'
import BasePageHeaderActions from '@/components/layout/BasePageHeaderActions.vue'
import BaseBreadcrumbs from '@/components/navigation/BaseBreadcrumbs.vue'
import { useTopBar } from '@/composables/useTopBar'

provide('topBarActionSize', 'xs')

const route = useRoute()
const {
  title: topBarTitle,
  subtitle: topBarSubtitle,
  breadcrumbs: topBarBreadcrumbsRef,
  actions: topBarActionsRef,
  actionsFromSlot,
  setTeleportTargetReady,
  reconcileTopBarForRoute,
} = useTopBar()

/** Deterministic reconcile on every navigation (DD-PAGE-HEADER-STALE-FIX). */
watch(
  () => route.fullPath,
  (path) => {
    reconcileTopBarForRoute(path)
  },
  { flush: 'post' }
)

const sidebarCollapse = inject<{
  isCollapsed: { value: boolean }
  toggle: () => void
  showToggle: { value: boolean }
} | null>('sidebarCollapse', null)

const layoutSidebarToggle = inject<(() => void) | null>('layoutSidebarToggle', null)

const layoutCollapsedStyle = inject<{ value: boolean } | null>('layoutCollapsedStyle', null)
const isSidebarCollapsed = computed(() => sidebarCollapse?.isCollapsed?.value ?? false)
const showCollapseToggle = computed(() => sidebarCollapse?.showToggle?.value ?? false)

function onSidebarToggle() {
  if (layoutSidebarToggle) {
    layoutSidebarToggle()
  } else {
    sidebarCollapse?.toggle?.()
  }
}

const topBarBreadcrumbItems = computed(() => topBarBreadcrumbsRef.value ?? [])

const hasTopBarBreadcrumbs = computed(
  () => topBarBreadcrumbItems.value.length > 0
)

const displayTitle = computed(() => {
  if (topBarTitle.value) return topBarTitle.value
  const metaTitle = route.meta?.title as string | undefined
  if (metaTitle) return metaTitle
  return 'Dashboard'
})

const displaySubtitle = computed(() => {
  if (hasTopBarBreadcrumbs.value) return null
  return topBarSubtitle.value
})

const topBarActions = computed(() => topBarActionsRef.value ?? [])

const visibleTopBarActions = computed(() =>
  topBarActions.value
    .filter(action => {
      if (action.show === undefined) return true
      if (typeof action.show === 'boolean') return action.show
      if (typeof action.show === 'function') return action.show()
      return true
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
)

const topBarActionsWithVariants = computed(() => {
  const actions = visibleTopBarActions.value
  if (actions.length === 0) return []
  const createIndex = actions.findIndex(
    a => a.label.toLowerCase() === 'create'
  )
  const primaryIndex = actions.findIndex(a => a.variant === 'primary')
  const singlePrimaryIndex =
    createIndex >= 0 ? createIndex : primaryIndex >= 0 ? primaryIndex : -1
  return actions.map((action, index) => ({
    ...action,
    variant:
      index === singlePrimaryIndex ? ('primary' as const) : ('ghost' as const)
  }))
})

onMounted(async () => {
  await nextTick()
  setTeleportTargetReady(true)
})

onUnmounted(() => {
  setTeleportTargetReady(false)
})
</script>
